import { FormEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login as authSliceLogin } from "../../../redux/features/authSlice";
import { useCreateAccountMutation } from "../../../redux/services/onboarding";
import AuthActionButton from "../components/auth-action-button";
import AuthErrorMessage from "../components/auth-error-message";
import AuthFormInput from "../components/auth-form-input";
import AuthFormTitle from "../components/auth-form-title";
import AuthSuccessMessage from "../components/auth-success-message";

const CompleteSignUp = ({ token }: { token: string }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createAccount] = useCreateAccountMutation();

  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error" | "token-error"
  >("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");

    if (status === "token-error") {
      navigate("/sign-up");
      return;
    }

    const formData = new FormData(e.currentTarget);

    // Simulate API call
    const data = {
      user: {
        firstName: formData.get("firstName") as string,
        lastName: formData.get("lastName") as string,
        password: formData.get("password") as string,
      },
      company: {
        name: formData.get("companyName") as string,
      },
    };

    try {
      const res = await createAccount({
        token,
        data,
      });

      if (res.data) {
        dispatch(authSliceLogin());
        navigate("/news-agents/new");
      } else if (res.error) {
        const tokenError =
          (res.error as any)?.data?.code === 1005 ||
          (res.error as any)?.message?.includes("token");
        setStatus(tokenError ? "token-error" : "error");
        setError(
          tokenError
            ? "The registration link is invalid or has expired. Please request a new one."
            : "An unexpected error occurred. Please try again."
        );
      }
    } catch (error) {
      setError(
        error?.data?.code === "TOKEN_INVALID"
          ? "The registration link is invalid or has expired. Please request a new one."
          : "An unexpected error occurred. Please try again."
      );
    }
  };

  if (status === "success") {
    return (
      <div className="mb-3">
        <AuthSuccessMessage
          title="Sign-up complete!"
          message="You can now log in to your account."
        />
      </div>
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex flex-column gap-3">
        <AuthFormTitle label="Complete your sign-up" />

        <AuthFormInput
          id="companyName"
          name="companyName"
          type="text"
          label="Company"
          placeholder="Enter your company name"
          required
        />

        <div className="grid">
          <div className="col-12 md:col-6">
            <AuthFormInput
              id="firstName"
              name="firstName"
              type="text"
              label="First Name"
              placeholder="Enter your first name"
              required
            />
          </div>
          <div className="col-12 md:col-6">
            <AuthFormInput
              id="lastName"
              name="lastName"
              type="text"
              label="Last Name"
              placeholder="Enter your last name"
              required
            />
          </div>
        </div>

        <AuthFormInput
          id="password"
          name="password"
          type="password"
          label="Password"
          placeholder="Create a password"
          required
          minLength={8}
          helpText="Password must be at least 8 characters long"
        />

        <AuthErrorMessage error={error} />

        <AuthActionButton
          label={status === "token-error" ? "Restart" : "Complete Sign Up"}
          disabled={status === "submitting"}
          loading={status === "submitting"}
        />
      </form>
    </div>
  );
};

export default CompleteSignUp;
